import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useStatus } from "../hooks/useStatus";
import { useFilters, type BridgeSort } from "../hooks/useFilters";
import OverallStatusBanner, {
  type AffectedBridge,
} from "../components/OverallStatusBanner";
import SiteCard from "../components/SiteCard";
import RefreshIndicator from "../components/RefreshIndicator";
import FilterBar from "../components/FilterBar";
import HistoryTab from "../components/HistoryTab";
import AppHeader from "../components/AppHeader";
import PageTabs from "../components/PageTabs";
import { REFETCH_INTERVAL_MS } from "../lib/constants";
import type {
  BridgeStatusDto,
  HealthStatus,
  SiteStatusDto,
  StatusResponse,
} from "../types/api";

/**
 * Skeleton card displayed while the first /api/status response is in flight.
 * Three skeletons match the contract guarantee that `sites` is always length 3.
 */
function SiteCardSkeleton() {
  return (
    <div
      className="rounded-2xl border border-surface-border bg-surface p-5 shadow-sm dark:border-surface-darkBorder dark:bg-surface-darkSubtle"
      aria-hidden="true"
    >
      <div className="flex items-center justify-between">
        <div className="h-5 w-32 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-5 w-20 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="mt-5 space-y-3">
        <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800/70" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-slate-100 dark:bg-slate-800/70" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100 dark:bg-slate-800/70" />
      </div>
    </div>
  );
}

// ─── Sorting helpers ──────────────────────────────────────────────────────────

// Priority for the "장애 우선" sort. STALE/RETIRED are intentionally at the bottom —
// they're effectively decommissioned and should sink even when sorting by severity.
const STATUS_PRIORITY: Record<HealthStatus, number> = {
  DOWN: 0,
  UNKNOWN: 1,
  UP: 2,
  STALE: 3,
  RETIRED: 4,
};

// Priority for the default "triage" sort. RETIRED is the very bottom — these
// lots were explicitly closed by operators, so they're pure noise for triage.
const TRIAGE_PRIORITY: Record<HealthStatus, number> = {
  DOWN: 0,
  UP: 1,
  UNKNOWN: 2,
  STALE: 3,
  RETIRED: 4,
};

function compareByName(a: BridgeStatusDto, b: BridgeStatusDto): number {
  const an = (a.lotName?.trim() || a.lotId).toLowerCase();
  const bn = (b.lotName?.trim() || b.lotId).toLowerCase();
  return an.localeCompare(bn, "ko");
}

function tsOrNull(s: string | null): number | null {
  if (!s) return null;
  const ms = new Date(s).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * "가장 오래된 갱신이 위" 정렬. null lastUpdatedAt 은 가장 위 (관측 자체가 없는 lot 은
 * 가장 의심스러우므로 노출 우선).
 */
function compareOldestFirst(
  a: BridgeStatusDto,
  b: BridgeStatusDto,
): number {
  const at = tsOrNull(a.lastUpdatedAt);
  const bt = tsOrNull(b.lastUpdatedAt);
  if (at === bt) return 0;
  if (at == null) return -1;
  if (bt == null) return 1;
  return at - bt;
}

/**
 * Sort bridges in-place by the user-selected order. Returns a new array so
 * callers don't mutate the original.
 */
function sortBridges(
  list: BridgeStatusDto[],
  sort: BridgeSort,
): BridgeStatusDto[] {
  const copy = [...list];
  switch (sort) {
    case "triage":
      // 기본 정렬: DOWN(데이터 정체, 위) → UP(마지막 갱신 오래된 순) → UNKNOWN → STALE(맨 아래).
      // 같은 등급 안에서는 lastUpdatedAt 오래된 순으로 한 번 더 정렬하여 위험 신호를 위로 모은다.
      return copy.sort((a, b) => {
        const ap = TRIAGE_PRIORITY[a.status] ?? 99;
        const bp = TRIAGE_PRIORITY[b.status] ?? 99;
        if (ap !== bp) return ap - bp;
        const byTs = compareOldestFirst(a, b);
        if (byTs !== 0) return byTs;
        return compareByName(a, b);
      });
    case "name-asc":
      return copy.sort(compareByName);
    case "name-desc":
      return copy.sort((a, b) => -compareByName(a, b));
    case "updated-oldest":
      return copy.sort(compareOldestFirst);
    case "updated-newest":
      // null goes to bottom — 최신 순에서 unknown은 가장 아래.
      return copy.sort((a, b) => {
        const at = tsOrNull(a.lastUpdatedAt);
        const bt = tsOrNull(b.lastUpdatedAt);
        if (at === bt) return 0;
        if (at == null) return 1;
        if (bt == null) return -1;
        return bt - at;
      });
    case "status-down-first":
      return copy.sort((a, b) => {
        const ap = STATUS_PRIORITY[a.status] ?? 99;
        const bp = STATUS_PRIORITY[b.status] ?? 99;
        if (ap !== bp) return ap - bp;
        // tie-break by name for stability
        return compareByName(a, b);
      });
    default:
      return copy;
  }
}

/**
 * Top-level status page composing all the pieces.
 *
 * Data flow:
 *   useStatus() ──┬── overall + generatedAt ──► OverallStatusBanner
 *                 ├── sites[] ──► filter/sort ──► SiteCard × N
 *                 └── dataUpdatedAt, isFetching, refetch ──► RefreshIndicator
 *
 * Filter/sort flow:
 *   useFilters() ──► FilterBar (UI) ──► applied to sites/bridges in this page
 *
 * Error handling philosophy:
 *   - Show last successful data even when a refetch fails (TanStack Query keeps
 *     `data` on transient errors thanks to placeholderData: prev).
 *   - Display a discreet `staleHint` on the banner so the user knows the
 *     connection is shaky.
 *   - Only the *initial* error (no data yet) shows the full error block.
 */
export default function StatusPage() {
  const { data, error, isLoading, isFetching, dataUpdatedAt, refetch } =
    useStatus();
  const { filters, setSite, setStatus, setSort, reset } = useFilters();
  // 뷰는 URL 이 결정한다(탭 state 아님). 새로고침/딥링크 시 보던 화면 유지.
  const location = useLocation();
  const tab: "live" | "history" = location.pathname.startsWith("/history")
    ? "history"
    : "live";

  const status: StatusResponse | undefined = data;
  const hasInitialError = !status && !!error;

  // Apply site/status filters + bridge sort. Memoized so we don't re-sort on
  // every 1-second tick driven by useNow.
  const processedSites = useMemo(() => {
    if (!status) return [];
    const filteredSites: SiteStatusDto[] =
      filters.site === "all"
        ? status.sites
        : status.sites.filter((s) => s.siteKey === filters.site);

    return filteredSites.map((site) => {
      const statusFiltered: BridgeStatusDto[] =
        filters.status === "all"
          ? site.bridges
          : site.bridges.filter((b) => b.status === filters.status);
      const sorted = sortBridges(statusFiltered, filters.sort);
      const filteredEmpty =
        site.bridges.length > 0 && sorted.length === 0;
      return { site, bridges: sorted, filteredEmpty };
    });
  }, [status, filters.site, filters.status, filters.sort]);

  // Flatten the urgent bridges (DOWN / UNKNOWN) across every site so the banner
  // can show a short preview. STALE/RETIRED are intentionally excluded —
  // they're effectively decommissioned and aren't "장애".
  // Always derived from the full response, not the filtered view, so toggling
  // a status filter doesn't hide the banner summary.
  const affected: AffectedBridge[] = useMemo(() => {
    if (!status) return [];
    const out: AffectedBridge[] = [];
    for (const s of status.sites) {
      for (const b of s.bridges) {
        if (b.status === "DOWN" || b.status === "UNKNOWN") {
          out.push({
            lotId: b.lotId,
            lotName: b.lotName,
            siteDisplayName: s.displayName,
            lastUpdatedAt: b.lastUpdatedAt,
            status: b.status,
          });
        }
      }
    }
    // Oldest-updated first → the bridge whose data has been silent longest is
    // probably the most urgent. UNKNOWN (lastUpdatedAt = null) sorts to the top.
    out.sort((a, b) => {
      const at = a.lastUpdatedAt ? new Date(a.lastUpdatedAt).getTime() : -1;
      const bt = b.lastUpdatedAt ? new Date(b.lastUpdatedAt).getTime() : -1;
      return at - bt;
    });
    return out;
  }, [status]);

  return (
    <div className="min-h-full bg-slate-50 text-slate-900 dark:bg-surface-dark dark:text-slate-100">
      <AppHeader
        right={
          <RefreshIndicator
            dataUpdatedAt={dataUpdatedAt}
            isFetching={isFetching}
            refetchIntervalMs={REFETCH_INTERVAL_MS}
            onRefresh={() => {
              void refetch();
            }}
          />
        }
      />

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-12">
        <PageTabs />

        {tab === "history" ? (
          <HistoryTab />
        ) : hasInitialError ? (
          <div
            role="alert"
            className="rounded-2xl border border-status-majorOutage/30 bg-status-majorOutage-bg px-6 py-6 text-status-majorOutage-fg dark:border-status-majorOutage-dark/30 dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg"
          >
            <h2 className="text-base font-semibold">
              상태 정보를 불러오지 못했습니다
            </h2>
            <p className="mt-1 text-sm">
              서버에 도달할 수 없습니다. 잠시 후 다시 시도해주세요.
            </p>
            <button
              type="button"
              onClick={() => {
                void refetch();
              }}
              className="mt-3 inline-flex rounded-md border border-current bg-transparent px-3 py-1.5 text-xs font-medium hover:bg-white/30 dark:hover:bg-black/20"
            >
              다시 시도
            </button>
          </div>
        ) : (
          <>
            <OverallStatusBanner
              overall={status?.overall ?? "UNKNOWN"}
              generatedAt={status?.generatedAt}
              staleHint={!!error && !!status}
              affected={affected}
            />

            <div className="mt-6">
              <FilterBar
                filters={filters}
                onChangeSite={setSite}
                onChangeStatus={setStatus}
                onChangeSort={setSort}
                onReset={reset}
              />
            </div>

            <section
              aria-label="사이트별 상태"
              className="mt-6 grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2"
            >
              {isLoading || !status ? (
                <>
                  <SiteCardSkeleton />
                  <SiteCardSkeleton />
                  <SiteCardSkeleton />
                </>
              ) : processedSites.length === 0 ? (
                <div className="rounded-2xl border border-surface-border bg-surface px-6 py-8 text-center text-sm text-slate-500 dark:border-surface-darkBorder dark:bg-surface-darkSubtle dark:text-slate-400">
                  선택한 필터에 해당하는 사이트가 없습니다.
                </div>
              ) : (
                processedSites.map(({ site, bridges, filteredEmpty }) => (
                  <SiteCard
                    key={site.siteKey}
                    site={site}
                    bridges={bridges}
                    filteredEmpty={filteredEmpty}
                    referenceNow={status.generatedAt}
                  />
                ))
              )}
            </section>
          </>
        )}

        <footer className="mt-10 flex flex-col gap-1 border-t border-surface-border pt-6 text-xs text-slate-500 dark:border-surface-darkBorder dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <div className="tabular-nums">
            {status
              ? `정체 주간 ${status.downAfterHoursLarge}h(${status.spaceThreshold}면↑)/${status.downAfterHoursSmall}h(${status.spaceThreshold}면↓)·야간 ${status.downAfterHoursNight}h · 미운영 ${status.staleAfterDays}d · 서버 폴링 ${Math.round(
                  status.pollIntervalMs / 1000,
                )}초 · UI 갱신 ${REFETCH_INTERVAL_MS / 1000}초`
              : `UI 갱신 ${REFETCH_INTERVAL_MS / 1000}초`}
          </div>
          <div>vestellalab · bridgeChecker</div>
        </footer>
      </main>
    </div>
  );
}
